import {
  experimental_streamedQuery as streamedQuery,
  queryOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import type { DatasetKey, Datasets } from '@infinex/types';

import { useAuthedQuery } from '../../session';
import type { GenericDatasetState } from '../types';
import { UserContext } from '../UserContext';

export const datasetQueryKeys = {
  all: ['datasets'] as const,
  dataset: <K extends DatasetKey>(key: K) => ['datasets', key] as const,
};

/** Time to wait for WebSocket response before falling back to TRPC */
const FALLBACK_TIMEOUT_MS = 3000;

/**
 * Default query options for datasets.
 * Consumers can override these using queryClient.setQueryDefaults(datasetQueryKeys.all, {...})
 */
const DEFAULT_DATASET_QUERY_OPTIONS = {
  /** Data is always fresh because WebSocket pushes updates */
  staleTime: Infinity,
  /** Keep unused data in cache for quick re-mount */
  gcTime: 1000 * 60 * 60 * 24,
};

export type DatasetState<K extends DatasetKey> = GenericDatasetState<
  Datasets[K]
>;

/**
 * Uses React Query's experimental streamedQuery to:
 * 1. Request data via WebSocket (with TRPC fallback on timeout)
 * 2. Yield initial data to the cache
 * 3. Keep yielding real-time updates as they arrive via WebSocket
 *
 * The stream stays open until the component unmounts or the query is
 * invalidated. When a consumer re-mounts after the stream was aborted, the
 * hook resubscribes (see the liveness effect below) — without it the cached
 * frame is fresh forever (`staleTime: Infinity`) and the query would never
 * re-open the stream, leaving the surface frozen on stale data.
 *
 * Query options (staleTime, gcTime, refetchOnMount, etc.) should be configured
 * at the QueryClient level using `queryClient.setQueryDefaults(datasetQueryKeys.all, {...})`.
 */
export const useDataset = <K extends DatasetKey>(key: K): DatasetState<K> => {
  const context = useContext(UserContext);
  const queryClient = useQueryClient();

  const { streamDataset } = context;

  const { data, status, fetchStatus } = useAuthedQuery(
    queryOptions({
      queryKey: datasetQueryKeys.dataset(key),
      queryFn: streamedQuery({
        // streamFn returns an AsyncIterable that yields data chunks
        streamFn: (ctx: { signal: AbortSignal }) =>
          streamDataset(key, FALLBACK_TIMEOUT_MS, ctx.signal),
        // On invalidation, abort the old stream and start fresh, but keep
        // serving the previous data until the new stream's first chunk
        // arrives ('reset' would blank the cache and flash every consumer
        // back to a loading state on each invalidation). With the
        // replace-with-latest reducer below, 'append' means "hold last frame,
        // then replace" — the restarted stream still begins with a full
        // snapshot, so no stale-chunk mixing is possible.
        refetchMode: 'append',
        // Each chunk is the full dataset - just replace with latest
        reducer: (_acc: Datasets[K] | undefined, chunk: Datasets[K]) => chunk,
        initialValue: undefined as Datasets[K] | undefined,
      }),
      ...DEFAULT_DATASET_QUERY_OPTIONS,
    })
  );

  // Stream liveness: a healthy dataset stream holds the query at fetchStatus
  // 'fetching' for as long as it is open — the transport generator only
  // returns when its abort signal fires (last consumer unmounted, or a
  // refetch replaced it). So observing success + idle from a mounted
  // consumer means the stream is dead: the cached frame renders as fresh
  // forever and websocket pushes have no subscriber. This is exactly the
  // navigate-away-and-back case. Invalidate our own key to restart the
  // stream; with refetchMode 'append' the cached frame stays rendered until
  // fresh data arrives. While the restarted stream is opening, fetchStatus is
  // 'fetching', so this cannot loop.
  useEffect(() => {
    if (status === 'success' && fetchStatus === 'idle') {
      void queryClient.invalidateQueries({
        queryKey: datasetQueryKeys.dataset(key),
      });
    }
  }, [fetchStatus, key, queryClient, status]);

  // Map React Query status to our DatasetState format
  // Note: When query is disabled (no session), status is 'pending' but fetchStatus is 'idle'.
  // We must check fetchStatus to distinguish disabled queries from actually-loading queries.
  return useMemo((): DatasetState<K> => {
    if (status === 'pending' && fetchStatus === 'fetching') {
      return { status: 'loading' } as DatasetState<K>;
    }

    if (status === 'error') {
      return { status: 'error' } as DatasetState<K>;
    }

    if (data !== undefined) {
      const now = Date.now();
      return {
        status: 'loaded',
        data: data as Datasets[K],
        time: now,
        lastRefresh: now,
      };
    }

    return { status: 'idle' } as DatasetState<K>;
  }, [status, fetchStatus, data]);
};

/**
 * Like useDataset, but transforms the data with a mapping function.
 * Note: If mapFn is dynamic, wrap it in useCallback to avoid unnecessary recomputation.
 */
export function useMappedDataset<K extends DatasetKey, ReturnType>(
  key: K,
  mapFn: (v: Datasets[K]) => ReturnType
): GenericDatasetState<ReturnType> {
  const underlyingDataset = useDataset(key);

  return useMemo((): GenericDatasetState<ReturnType> => {
    switch (underlyingDataset.status) {
      case 'loaded': {
        const { data, ...rest } = underlyingDataset;
        return {
          ...rest,
          data: mapFn(data),
        };
      }
      default:
        const { data, ...rest } = underlyingDataset;
        return {
          ...rest,
          ...(data !== undefined && { data: mapFn(data) }),
        };
    }
  }, [underlyingDataset, mapFn]);
}

/**
 * Hook for manually updating or invalidating dataset caches.
 *
 * Use cases:
 * - Optimistic updates: `setDataset(key, optimisticData)`
 * - Force refresh: `invalidateDataset(key)`
 * - Clear on logout: `clearAllDatasets()`
 */
export function useDatasetCacheUpdater() {
  const queryClient = useQueryClient();

  /** Directly update a dataset in the cache (for optimistic updates) */
  const setDataset = useCallback(
    <K extends DatasetKey>(
      key: K,
      updater:
        | Datasets[K]
        | ((input: Datasets[K] | undefined) => Datasets[K] | undefined)
    ) => {
      queryClient.setQueryData(datasetQueryKeys.dataset(key), updater);
    },
    [queryClient]
  );

  /** Mark a dataset as stale and refetch (restarts the stream) */
  const invalidateDataset = useCallback(
    <K extends DatasetKey>(key: K) => {
      void queryClient.invalidateQueries({
        queryKey: datasetQueryKeys.dataset(key),
      });
    },
    [queryClient]
  );

  /** Invalidate all datasets */
  const invalidateAllDatasets = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: datasetQueryKeys.all,
    });
  }, [queryClient]);

  /** Remove all datasets from cache entirely */
  const clearAllDatasets = useCallback(() => {
    queryClient.removeQueries({
      queryKey: datasetQueryKeys.all,
    });
  }, [queryClient]);

  return {
    setDataset,
    invalidateDataset,
    invalidateAllDatasets,
    clearAllDatasets,
  };
}
